import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarDays, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAvailableSlots } from "@/lib/sessionQueries";
import { useBookTeamSession } from "@/lib/teamRecordQueries";
import AddTeamSessionsModal from "./AddTeamSessionsModal";

/**
 * Clients Restructure — Team 360 -> Sessions -> "Book Session". Same
 * real-TidyCal-availability flow as Client 360's BookSessionModal
 * (session_repository.book_team_session_directly), plus a participant
 * multi-select — ONE canonical Session with every selected member in
 * participant_member_ids, never one Session per member.
 */
export default function BookTeamSessionModal({ workspaceId, teamName, teamMembers, onClose }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientName, setClientName] = useState(teamName || "");
  const [clientEmail, setClientEmail] = useState("");
  const [participantIds, setParticipantIds] = useState(teamMembers.map((m) => m.membership_id));
  const [showAddCredits, setShowAddCredits] = useState(false);

  const rangeStartsAt = useMemo(() => new Date().toISOString(), []);
  const rangeEndsAt = useMemo(() => new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), []);
  const { data, isLoading, isError } = useAvailableSlots(workspaceId, { startsAt: rangeStartsAt, endsAt: rangeEndsAt });
  const bookSession = useBookTeamSession(workspaceId);

  const slotsByDate = useMemo(() => {
    const groups = {};
    for (const slot of data?.slots || []) {
      const day = new Date(slot.starts_at).toDateString();
      (groups[day] = groups[day] || []).push(slot);
    }
    return groups;
  }, [data]);
  const dateKeys = Object.keys(slotsByDate);

  function toggleParticipant(id) {
    setParticipantIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  const canConfirm = !!selectedSlot && clientName.trim().length > 0 && clientEmail.trim().length > 2 && participantIds.length >= 1;

  async function handleConfirm() {
    if (!canConfirm) return;
    try {
      await bookSession.mutateAsync({
        starts_at: selectedSlot.starts_at, client_name: clientName.trim(), client_email: clientEmail.trim(),
        participant_member_ids: participantIds,
      });
      toast.success(`Team session booked — ${new Date(selectedSlot.starts_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`);
      onClose();
    } catch (err) {
      if (err.status === 402) {
        setShowAddCredits(true);
        return;
      }
      console.error("Book Team Session failed:", err.body?.detail || err.message);
      toast.error("Couldn't confirm this time with TidyCal. Please try a different slot or try again shortly.");
    }
  }

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl" data-testid="book-team-session-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Book team session</DialogTitle>
            <DialogDescription>Pick a real available time — books it directly, with the selected members as participants.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs text-ink-soft uppercase mb-1.5 block">Participants</label>
              <div className="grid grid-cols-2 gap-2" data-testid="book-team-session-participants-grid">
                {teamMembers.map((m) => {
                  const selected = participantIds.includes(m.membership_id);
                  return (
                    <button
                      key={m.membership_id}
                      type="button"
                      onClick={() => toggleParticipant(m.membership_id)}
                      className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm text-left transition-colors ${selected ? "border-terracotta bg-terracotta/10 text-ink" : "border-ink/10 text-ink-soft hover:border-ink/20"}`}
                      data-testid={`book-team-session-participant-${m.membership_id}`}
                    >
                      <span>{m.display_name}</span>
                      {selected && <Check size={14} className="text-terracotta shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-ink-soft py-6 justify-center" data-testid="book-team-session-slots-loading">
                <Loader2 size={16} className="animate-spin" /> Loading real availability…
              </div>
            )}
            {isError && <p className="text-sm text-red-600 py-2" data-testid="book-team-session-slots-error">Couldn't load available times from TidyCal. Try again in a moment.</p>}
            {!isLoading && !isError && dateKeys.length === 0 && <p className="text-sm text-ink-soft py-2" data-testid="book-team-session-slots-empty">No available times in the next 14 days.</p>}

            {dateKeys.length > 0 && (
              <div>
                <label className="text-xs text-ink-soft uppercase mb-1.5 block flex items-center gap-1.5"><CalendarDays size={13} /> Date</label>
                <div className="flex flex-wrap gap-1.5" data-testid="book-team-session-date-chips">
                  {dateKeys.map((day) => (
                    <button
                      key={day}
                      onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                      className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${selectedDate === day ? "bg-terracotta text-paper border-terracotta" : "border-ink/15 text-ink-soft hover:border-terracotta/50"}`}
                      data-testid={`book-team-session-date-chip-${day.replace(/\s/g, "-")}`}
                    >
                      {new Date(day).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedDate && (
              <div>
                <label className="text-xs text-ink-soft uppercase mb-1.5 block">Time</label>
                <div className="flex flex-wrap gap-1.5" data-testid="book-team-session-time-chips">
                  {slotsByDate[selectedDate].map((slot) => (
                    <button
                      key={slot.starts_at}
                      onClick={() => setSelectedSlot(slot)}
                      className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${selectedSlot?.starts_at === slot.starts_at ? "bg-terracotta text-paper border-terracotta" : "border-ink/15 text-ink-soft hover:border-terracotta/50"}`}
                      data-testid={`book-team-session-time-chip-${slot.starts_at}`}
                    >
                      {new Date(slot.starts_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedSlot && (
              <div className="border border-ink/10 rounded-lg p-4 space-y-3" data-testid="book-team-session-contact-details">
                <p className="text-sm font-medium text-ink">
                  {new Date(selectedSlot.starts_at).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
                <div>
                  <label className="text-xs text-ink-soft uppercase mb-1.5 block">Contact name (for TidyCal)</label>
                  <Input value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="book-team-session-name-input" />
                </div>
                <div>
                  <label className="text-xs text-ink-soft uppercase mb-1.5 block">Contact email (for TidyCal)</label>
                  <Input value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} data-testid="book-team-session-email-input" />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="text-sm text-ink-soft px-4 py-2 rounded-md hover:bg-ink/5" data-testid="book-team-session-cancel-btn">Cancel</button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm || bookSession.isPending}
              className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
              data-testid="book-team-session-confirm-btn"
            >
              {bookSession.isPending ? "Booking…" : "Book team session"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {showAddCredits && (
        <AddTeamSessionsModal
          workspaceId={workspaceId}
          onClose={() => setShowAddCredits(false)}
          onSuccess={() => { setShowAddCredits(false); handleConfirm(); }}
          nested
        />
      )}
    </>
  );
}
